import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Clock,
  FileText,
  Briefcase,
  ShieldCheck,
  Lightbulb,
  Search,
} from "lucide-react";
import { Container, CardMotion } from "@/components/site/Primitives";
import { CategoryPill } from "@/components/site/CategoryPill";
import { NewsletterSubscribeForm } from "@/components/site/NewsletterSubscribeForm";
import { loadInsights, loadFeaturedInsightTags } from "@/lib/content-loader";
import {
  INSIGHT_FILTER_CATEGORIES,
  collectPopularTopics,
  insightDisplayLabel,
  insightMatchesFilter,
  insightMatchesSearch,
  insightMatchesTopic,
  resolveInsightFilterFromCategory,
} from "@/lib/insights/categories";
import { formatPublicDateShort } from "@/lib/format-public-date";
import heroPenangAsset from "@/assets/penang-georgetown-sunset-hero.png.asset.json";

export const Route = createFileRoute("/insights/")({
  validateSearch: (search: Record<string, unknown>) => ({
    category: typeof search.category === "string" ? search.category : "",
  }),
  loader: async () => ({
    articles: await loadInsights(),
    featuredTags: await loadFeaturedInsightTags(),
  }),
  head: () => ({
    meta: [
      { title: "Insights — Tax, Audit & Regulatory Updates | KS Lau & Co" },
      {
        name: "description",
        content:
          "Timely updates, practical perspectives and expert analysis from KS Lau & Co to help businesses navigate Malaysia's evolving regulatory landscape.",
      },
      { property: "og:title", content: "Insights — KS Lau & Co" },
      {
        property: "og:description",
        content:
          "Thought leadership on Malaysian tax, audit and regulatory matters.",
      },
    ],
  }),
  component: InsightsPage,
});

const CATEGORY_HIGHLIGHTS = [
  {
    Icon: FileText,
    title: "Tax Updates",
    body: "Stay informed on the latest tax changes and what they mean for your business.",
  },
  {
    Icon: Briefcase,
    title: "Business Advisory",
    body: "Insights and strategies to help you grow, adapt and perform with confidence.",
  },
  {
    Icon: ShieldCheck,
    title: "Regulatory Updates",
    body: "Key regulatory and compliance updates impacting your business.",
  },
  {
    Icon: Lightbulb,
    title: "Thought Leadership",
    body: "Our perspectives on trends, opportunities and issues shaping the future.",
  },
];

const FILTERS = ["All Insights", ...INSIGHT_FILTER_CATEGORIES] as const;

function formatDate(d: string) {
  return formatPublicDateShort(d);
}

function InsightsPage() {
  const { articles, featuredTags } = Route.useLoaderData();
  const { category: categoryParam } = Route.useSearch();
  const initialFilter = resolveInsightFilterFromCategory(categoryParam);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>(initialFilter);
  const [query, setQuery] = useState("");
  const [topicFilter, setTopicFilter] = useState<string | null>(null);

  useEffect(() => {
    if (categoryParam) {
      setFilter(resolveInsightFilterFromCategory(categoryParam));
      setTopicFilter(null);
      setQuery("");
      document.getElementById("insights-results")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [categoryParam]);

  const featured = articles[0];

  const popularTopics = useMemo(() => collectPopularTopics(articles, featuredTags), [articles, featuredTags]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = articles.filter((a) => {
      const matchesFilter = insightMatchesFilter(a.category, filter);
      const matchesTopic = topicFilter ? insightMatchesTopic(a, topicFilter) : true;
      const matchesQuery = topicFilter ? true : insightMatchesSearch(a, query);
      return matchesFilter && matchesTopic && matchesQuery;
    });
    if (!q && filter === "All Insights" && featured) {
      return matches.filter((a) => a.slug !== featured.slug);
    }
    return matches;
  }, [filter, query, topicFilter, articles, featured]);

  return (
    <>
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <img
          src={heroPenangAsset.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-r from-primary/90 via-primary/55 to-primary/10"
        />
        <Container>
          <div className="flex min-h-[32vh] flex-col justify-end py-6 text-primary-foreground md:min-h-[40vh] md:py-8">
            <h1 className="max-w-2xl text-4xl font-semibold leading-[1.1] tracking-tight md:text-5xl lg:text-6xl">
              Insights that
              <br />
              Keep You Ahead.
            </h1>
          </div>
        </Container>
      </section>

      {/* CATEGORY STRIP */}
      <section className="border-y border-border bg-stone/40">
        <Container>
          <div className="grid gap-8 py-10 md:grid-cols-4 md:gap-0">
            {CATEGORY_HIGHLIGHTS.map(({ Icon, title, body }, i) => (
              <div
                key={title}
                className={
                  "flex gap-4 md:px-6 " +
                  (i > 0 ? "md:border-l md:border-border" : "")
                }
              >
                <Icon
                  className="h-8 w-8 shrink-0 text-gold"
                  strokeWidth={1.5}
                />
                <div>
                  <h3 className="text-base font-semibold text-primary">
                    {title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* STICKY FILTER + SEARCH */}
      <div className="sticky top-16 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <Container>
          <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {FILTERS.map((f) => {
                const active = filter === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => {
                      setFilter(f);
                      setTopicFilter(null);
                    }}
                    className={
                      "transition " +
                      (active
                        ? "rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground"
                        : "px-1 py-1.5 text-sm font-medium text-muted-foreground hover:text-primary")
                    }
                  >
                    {f}
                  </button>
                );
              })}
            </div>
            <label className="relative flex items-center md:w-56">
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setTopicFilter(null);
                }}
                placeholder="Search insights…"
                className="w-full rounded-full border border-border bg-white py-1.5 pl-4 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
              <Search className="pointer-events-none absolute right-3 h-4 w-4 text-muted-foreground" />
            </label>
          </div>
        </Container>
      </div>

      {/* FEATURED INSIGHT */}
      {featured && filter === "All Insights" && !query.trim() && !topicFilter && (
        <section className="bg-white py-10 md:py-12">
          <Container>
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <span className="text-sm font-semibold uppercase tracking-[0.12em] text-gold">
                  Featured Insight
                </span>
                <h2 className="mt-3 text-2xl leading-tight text-primary md:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {featured.excerpt}
                </p>
                <Link
                  to="/insights/$slug"
                  params={{ slug: featured.slug }}
                  aria-label={`Read more: ${featured.title}`}
                  className="mt-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold bg-white text-gold transition hover:bg-gold hover:text-primary-foreground"
                >
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
                <div className="mt-5 flex items-center gap-3 text-sm text-muted-foreground">
                  <span>{formatDate(featured.date)}</span>
                  <span className="text-border">|</span>
                  <span>{insightDisplayLabel(featured)}</span>
                  <span className="text-border">|</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> {featured.readTime}
                  </span>
                </div>
              </div>
              <Link
                to="/insights/$slug"
                params={{ slug: featured.slug }}
                className="group block overflow-hidden rounded-lg"
              >
                <img
                  src={featured.cover}
                  alt={featured.title}
                  loading="lazy"
                  width={1280}
                  height={640}
                  className="aspect-[16/10] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                />
              </Link>
            </div>
          </Container>
        </section>
      )}

      {/* ARTICLE GRID */}
      <section id="insights-results" className="py-14 md:py-16">
        <Container>
          {filtered.length === 0 ? (
            <p className="py-12 text-center text-base text-muted-foreground">
              No insights match your search.
            </p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {filtered.map((a, i) => (
                <Link
                  key={a.slug}
                  to="/insights/$slug"
                  params={{ slug: a.slug }}
                  className="group block h-full"
                >
                  <CardMotion
                    animateIndex={i}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition hover:shadow-md"
                  >
                  <div className="relative overflow-hidden">
                    <img
                      src={a.cover}
                      alt={a.title}
                      loading="lazy"
                      width={800}
                      height={500}
                      className="aspect-[4/3] w-full object-cover transition duration-700 group-hover:scale-[1.04]"
                    />
                    <div className="absolute bottom-3 left-3">
                      <CategoryPill label={insightDisplayLabel(a)} variant="overlay" />
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="text-lg leading-snug text-primary">
                      {a.title}
                    </h3>
                    <p className="mt-3 flex-1 text-base leading-relaxed text-muted-foreground">
                      {a.excerpt}
                    </p>
                    <div className="mt-5 flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
                      <span>{formatDate(a.date)}</span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" /> {a.readTime}
                      </span>
                    </div>
                    <span className="mt-4 inline-flex items-center text-gold transition group-hover:translate-x-1">
                      <span className="sr-only">Read more</span>
                      <ArrowRight aria-hidden="true" className="h-4 w-4" />
                    </span>
                  </div>
                  </CardMotion>
                </Link>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* POPULAR TOPICS + NEWSLETTER */}
      <section className="pb-20 md:pb-24">
        <Container>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-stone/50 p-8 md:p-10">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">
                Popular Topics
              </span>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {popularTopics.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setFilter("All Insights");
                      setTopicFilter(t);
                      setQuery(t);
                      document.getElementById("insights-results")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="rounded-md border border-border bg-white px-4 py-2 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    {t}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  setFilter("All Insights");
                  setTopicFilter(null);
                  setQuery("");
                }}
                className="mt-8 inline-flex items-center gap-1.5 text-base font-semibold text-primary hover:underline"
              >
                View all topics <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="relative overflow-hidden bg-primary p-8 text-primary-foreground md:p-10">
              <div
                aria-hidden="true"
                className="absolute inset-0 -z-0 opacity-20"
                style={{
                  backgroundImage: `url(${heroPenangAsset.url})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="relative">
                <NewsletterSubscribeForm variant="dark" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CLOSING QUOTE */}
      <section className="border-t border-border py-16 md:py-20">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-2xl italic leading-relaxed text-primary md:text-3xl">
              <span className="text-gold">“</span>
              Knowledge empowers better decisions.
              <br />
              We're here to help you stay informed and ahead.
              <span className="text-gold">”</span>
            </p>
          </div>
        </Container>
      </section>
    </>
  );
}
